// dependencies
import React from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";

// fontawesome
import {
	faArrowUpFromSquare,
	faBallot,
	faBell,
	faCalendarDays,
	faChartNetwork,
	faChartPieSimple,
	faChartTreeMap,
	faFolder,
	faFolderArrowUp,
	faGear,
	faHouse,
	faMarker,
	faMessageLines,
	faMessages,
	faMicrochipAi,
	faPenField,
	faPollPeople,
	faShieldPlus,
	faTableList,
	faUser,
	faUsers,
	faWallet,
} from "@performyard/fontawesome/pro-regular-svg-icons";
import { faHeartPulse } from "@performyard/fontawesome/pro-solid-svg-icons";

// components
import VerticalNavBar from "../../components/VerticalNavBar";
import { NavItem } from "../../components/VerticalNavBar/SidebarItem";

import { useCurrentRoute } from "../../app/router";

//hooks
import { useRbacPrimaryRolePermissions } from "../../api/hooks";
import { useFeatureFlag, useFeatureFlagQuery } from "../../hooks/useFeatureFlag";

// selectors
import { getIsUserSuperuser, getUserPermissionType } from "../../selectors/app";
import { getCompanySettingsContext, getCustomBilling } from "../../selectors/settings";

import RouterTags from "../../constants/RouterTags";

const AdminVerticalNavBar = () => {
	const rootElement = document.getElementById("admin-nav-bar");
	const companyID = rootElement?.getAttribute("company") || "";

	// user company custom settings
	const isUserSuperuser = useSelector(getIsUserSuperuser);
	const companySettings = useSelector(getCompanySettingsContext);
	const isBillingAdmin = useSelector(getUserPermissionType) === "billing-admins";
	const hasCustomBilling = useSelector(getCustomBilling);

	// get feature enabled settings
	const { value: groupsFeatureVisible } = useFeatureFlagQuery("ff-is-groups-enabled", false);
	const groupsEnabled = companySettings.getIn(["groups", "state"], "disabled") === "enabled" && groupsFeatureVisible;

	const { value: conversationalAIFeatureVisible } = useFeatureFlagQuery("ff-is-conversational-ui-enabled", false);

	const reviewsFeatureVisible = useFeatureFlag("ff-is-reviews-enabled", true);
	const reviewsEnabled = companySettings.getIn(["reviews", "state"], "disabled") === "enabled";

	const { value: meetingsEnabled } = useFeatureFlagQuery("ff-is-meetings-enabled", false);

	const { value: goalsFeatureVisible } = useFeatureFlagQuery("ff-is-goals-enabled", false);
	const goalsEnabled = companySettings.getIn(["goals", "state"], "disabled") === "enabled";

	const { value: feedbackFeatureVisible } = useFeatureFlagQuery("ff-is-feedback-enabled", true);
	const feedbackEnabled =
		companySettings.getIn(["feedback", "state"], "disabled") === "enabled" && feedbackFeatureVisible;

	const { value: engagementSurveyEnabled } = useFeatureFlagQuery("ff-is-engagement-survey-enabled", false);
	const workflowsFeatureVisible = useFeatureFlag("ff-is-workflows-enabled", true);
	const workflowsEnabled = companySettings.getIn(["workflows", "state"], "disabled") === "enabled";
	const { value: docSigningFeatureVisible } = useFeatureFlagQuery("ff-is-document-signing-enabled", false);
	const docSigningEnabled =
		companySettings.getIn(["documentSigning", "state"], "disabled") === "enabled" && docSigningFeatureVisible;
	const integrationsEnabled = companySettings.getIn(["hrIntegration", "state"], "disabled") !== "disabled";
	const { value: sftpFeatureVisible } = useFeatureFlagQuery("ff-is-enabled-sftp-integration", false);
	const sftpEnabled =
		companySettings.getIn(["integrationSftp", "state"], "disabled") === "enabled" && sftpFeatureVisible;
	const { value: calendarIntegrationFeatureVisible } = useFeatureFlagQuery(
		"ff-is-calendar-integration-enabled",
		false
	);

	// SCIM
	const { value: scimFeatureVisible } = useFeatureFlagQuery("ff-is-scim-enabled", false);
	const scimEnabled = companySettings.getIn(["scim_api", "state"], "disabled") === "enabled" && scimFeatureVisible;

	// ai feature flag enabled
	const { value: aiFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);

	const { value: surveysEnabled } = useFeatureFlagQuery("ff-is-surveys-enabled", false);

	const companyLevelHiddenItems = {
		documents: companySettings ? companySettings.getIn(["documents", "state"], "enabled") === "hidden" : false,
		notifications: companySettings
			? companySettings.getIn(["notifications", "state"], "enabled") === "hidden"
			: false,
	};

	const hiddenItems = {
		...companyLevelHiddenItems,
		documents: !isBillingAdmin ? true : companyLevelHiddenItems.documents,
	};

	const currentRoute = useCurrentRoute();
	const shouldShowNav = !currentRoute?.route?.handle?.tags?.includes(RouterTags.HIDE_ADMIN_NAV);

	const { data: engagementPermissions, isLoading: isEngagementPermissionsLoading } =
		useRbacPrimaryRolePermissions("engagement");
	const userCanEditEngagement =
		!isEngagementPermissionsLoading && engagementPermissions?.includes("engagement:editSettings");

	const { data: scimPermissions, isLoading: isScimPermissionsLoading } = useRbacPrimaryRolePermissions("scim_api");
	const userCanEditScim = !isScimPermissionsLoading && scimPermissions?.includes("scim_api:viewScimApiToken");

	// function to get path based on if user is super user
	const getPath = (path: string, hasSuperUserPath: boolean) => {
		if (isUserSuperuser && hasSuperUserPath) {
			// administration is the only path that behaves a little differently
			if (path === "administration") {
				return `/super-user-admin/${companyID}/`;
			}
			return `/super-user-admin/${path}/${companyID}/`;
		} else {
			if (path === "administration") {
				return "/administration/";
			}
			if (path === "integrations") {
				return "/integrations/";
			}
			return `/administration/${path}/`;
		}
	};

	const AIChildren = [
		...(reviewsFeatureVisible && (reviewsEnabled || isUserSuperuser)
			? [
					{
						title: "AI Rephrase",
						path: getPath("ai-insights/ai-authoring-assistant", false),
					},
					{
						title: "AI Review Summarization",
						path: getPath("ai-insights/ai-review-summarization", false),
					},
				]
			: []),
		...(reviewsEnabled
			? [
					{
						title: "AI Review Cycle Insights",
						path: getPath("ai-insights/ai-review-cycle-insights", false),
					},
				]
			: []),
		...(engagementSurveyEnabled
			? [
					{
						title: "AI Engagement Analysis",
						path: getPath("ai-insights/ai-engagement-analysis", false),
					},
				]
			: []),
		...(meetingsEnabled || isUserSuperuser
			? [
					{
						title: "AI Meeting Workspace Insights",
						path: getPath("ai-insights/ai-meeting-workspace-insights", false),
					},
				]
			: []),
		...(surveysEnabled || isUserSuperuser
			? [
					{
						title: "AI Survey Insights",
						path: getPath("ai-insights/ai-survey-insights", false),
					},
				]
			: []),
	];

	const navItems = [
		{
			title: "Home",
			icon: faHouse,
			path: getPath("administration", true),
		},
		{
			title: "Employees",
			icon: faUser,
			path: getPath("employees", true),
		},
		{
			title: "Employee Data Fields",
			icon: faTableList,
			path: getPath("data-fields", true),
		},
		{
			title: "Employee Data Changes",
			icon: faBallot,
			path: getPath("field-changes", false),
		},
		groupsEnabled
			? {
					title: "Groups",
					icon: faUsers,
					path: getPath("groups", true),
				}
			: null,
		reviewsFeatureVisible && (reviewsEnabled || isUserSuperuser)
			? {
					title: "Reviews",
					icon: faPenField,
					children: [
						{ title: "Forms", path: getPath("reviews/forms", true) },
						{ title: "Templates", path: getPath("reviews/templates", true) },
						{ title: "Graph Settings", path: getPath("question-maps", false) },
					],
				}
			: null,
		goalsFeatureVisible && (goalsEnabled || isUserSuperuser)
			? { title: "Goals", icon: faChartPieSimple, path: getPath("goals", true) }
			: null,
		feedbackEnabled || isUserSuperuser
			? { title: "Feedback", icon: faMessageLines, path: getPath("feedback", true) }
			: null,
		conversationalAIFeatureVisible
			? { title: "Ask PerformYard", icon: faMessages, path: getPath("conversational-ai", false) }
			: null,
		aiFeatureFlagEnabled && (AIChildren.length > 0 || isUserSuperuser)
			? {
					title: "AI Insights",
					icon: faMicrochipAi,
					children: [...AIChildren],
				}
			: null,
		(engagementSurveyEnabled && userCanEditEngagement) || isUserSuperuser
			? {
					title: "Engagement",
					icon: faHeartPulse,
					children: [
						{ title: "Pulse Survey Settings", path: getPath("engagement", false) },
						{ title: "Employee Cohorts", path: getPath("engagement/cohorts", false) },
						{ title: "Excluded Employees", path: getPath("engagement/excluded-employees", false) },
						{ title: "Questions", path: getPath("engagement/questions", false) },
					],
				}
			: null,
		workflowsFeatureVisible && (workflowsEnabled || isUserSuperuser)
			? {
					title: "Workflows",
					icon: faChartTreeMap,
					path: getPath("workflows", true),
				}
			: null,
		calendarIntegrationFeatureVisible
			? { title: "Calendar Integration", icon: faCalendarDays, path: getPath("calendar-integration", false) }
			: null,
		integrationsEnabled
			? { title: "HRIS Integration", icon: faArrowUpFromSquare, path: getPath("integrations", false) }
			: null,
		sftpEnabled ? { title: "SFTP Setup", icon: faFolderArrowUp, path: getPath("sftp", false) } : null,
		userCanEditScim && scimEnabled
			? { title: "SCIM Setup", icon: faChartNetwork, path: getPath("scim", false) }
			: null,
		!hiddenItems.documents || isUserSuperuser
			? { title: "Documents", icon: faFolder, path: getPath("documents", true) }
			: null,
		!hiddenItems.notifications || isUserSuperuser
			? { title: "Notifications", icon: faBell, path: getPath("notifications", false) }
			: null,
		(isBillingAdmin || isUserSuperuser) && !hasCustomBilling
			? { title: "Billing", icon: faWallet, path: getPath("billing", true) }
			: null,
		docSigningEnabled
			? { title: "Signature Requests", icon: faMarker, path: getPath("signature-requests", true) }
			: null,
		{ title: "Authentication", icon: faShieldPlus, path: getPath("authentication-settings", false) },
		isUserSuperuser ? { title: "Company Setup", icon: faGear, path: getPath("setup", true) } : null,
		{ title: "Bulk User Logs", icon: faPollPeople, path: getPath("sync-log", false) },
	].filter((item: NavItem | null) => item !== null) as NavItem[];

	const content = shouldShowNav ? <VerticalNavBar navItems={navItems} /> : null;

	if (rootElement) {
		return ReactDOM.createPortal(content, rootElement);
	}
};

export default AdminVerticalNavBar;
